/**
 * SoleadLab 2.0 — Observation Entity API Client
 *
 * B3.2: Frontend service for observation entity CRUD.
 *
 * Read: fetchObservations (auto-paginated list), fetchSingleObservation
 * Write: createObservation, updateObservation (If-Match ETag concurrency)
 *
 * Observations are mutable with optimistic concurrency.
 * Backend version increments on each update.
 *
 * Follows the same per-service pattern as indicationApi.ts.
 */

import { apiFetch, apiFetchWithEtag } from './apiFetch';
export { isConflictError } from './apiFetch';

// ─── Types ──────────────────────────────────────────────────────────────────────

/** Raw row shape returned by the observation entity API */
export interface ObservationEntityRow {
  id: string;
  site_id: string;
  session_id: string | null;
  asset_id: string | null;
  evidence_id: string | null;
  source_type: string;
  observation_type: string;
  severity: string | null;
  geometry_json: string | null;
  metrics_json: string | null;
  topology_json: string | null;
  confidence: number;
  status: string;
  properties_json: string | null;
  version: number;
  created_at: string;
  updated_at: string;
  created_by: string | null;
  updated_by: string | null;
}

/** Paginated list response */
interface ObservationListResponse {
  items: ObservationEntityRow[];
  total: number;
  page: number;
  limit: number;
  hasMore: boolean;
}

/** Frontend observation shape (parsed JSON fields) */
export interface Observation {
  id: string;
  siteId: string;
  sessionId: string | null;
  assetId: string | null;
  evidenceId: string | null;
  sourceType: string;
  observationType: string;
  severity: string | null;
  geometry: GeoJSON.Geometry | null;
  metrics: Record<string, unknown> | null;
  topology: Record<string, string> | null;
  confidence: number;
  status: string;
  properties: Record<string, unknown> | null;
  version: number;
  createdAt: string;
  updatedAt: string;
  createdBy: string | null;
  updatedBy: string | null;
}

/** Filters for list endpoint */
export interface ObservationFilters {
  sourceType?: string;
  observationType?: string;
  assetId?: string;
  sessionId?: string;
  evidenceId?: string;
  severity?: string;
  status?: string;
}

// ─── Fetch Utilities (thin adapters around centralized apiFetch) ──────────────

async function apiFetchGet<T>(path: string): Promise<T> {
  return apiFetch<T>(path);
}

async function apiFetchMutate<T>(
  path: string,
  method: string,
  body?: unknown,
  extraHeaders?: Record<string, string>,
): Promise<{ data: T; etag: number }> {
  return apiFetchWithEtag<T>(path, {
    method,
    headers: extraHeaders,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
}

// ─── Transform ──────────────────────────────────────────────────────────────────

/** Entity row → frontend Observation shape */
function entityRowToObservation(row: ObservationEntityRow): Observation {
  let geometry: GeoJSON.Geometry | null = null;
  if (row.geometry_json) {
    try { geometry = JSON.parse(row.geometry_json); } catch { /* ignore */ }
  }

  let metrics: Record<string, unknown> | null = null;
  if (row.metrics_json) {
    try { metrics = JSON.parse(row.metrics_json); } catch { /* ignore */ }
  }

  let topology: Record<string, string> | null = null;
  if (row.topology_json) {
    try { topology = JSON.parse(row.topology_json); } catch { /* ignore */ }
  }

  let properties: Record<string, unknown> | null = null;
  if (row.properties_json) {
    try { properties = JSON.parse(row.properties_json); } catch { /* ignore */ }
  }

  return {
    id: row.id,
    siteId: row.site_id,
    sessionId: row.session_id,
    assetId: row.asset_id,
    evidenceId: row.evidence_id,
    sourceType: row.source_type,
    observationType: row.observation_type,
    severity: row.severity,
    geometry,
    metrics,
    topology,
    confidence: row.confidence,
    status: row.status,
    properties,
    version: row.version,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    createdBy: row.created_by,
    updatedBy: row.updated_by,
  };
}

/** Frontend Observation → entity API body shape for create */
function observationToCreateBody(obs: Partial<Observation> & { sourceType: string; observationType: string }): Record<string, unknown> {
  const body: Record<string, unknown> = {
    source_type: obs.sourceType,
    observation_type: obs.observationType,
  };
  if (obs.id) body.id = obs.id;
  if (obs.sessionId) body.session_id = obs.sessionId;
  if (obs.assetId) body.asset_id = obs.assetId;
  if (obs.evidenceId) body.evidence_id = obs.evidenceId;
  if (obs.severity) body.severity = obs.severity;
  if (obs.status) body.status = obs.status;
  if (obs.confidence !== undefined) body.confidence = obs.confidence;
  if (obs.geometry) body.geometry_json = JSON.stringify(obs.geometry);
  if (obs.metrics) body.metrics_json = JSON.stringify(obs.metrics);
  if (obs.topology) body.topology_json = JSON.stringify(obs.topology);
  if (obs.properties) body.properties_json = JSON.stringify(obs.properties);
  return body;
}

// ─── Read API ───────────────────────────────────────────────────────────────────

/**
 * Fetch ALL observations for a site. Auto-paginates (200/page).
 * Supports optional filters.
 */
export async function fetchObservations(
  siteId: string,
  filters?: ObservationFilters,
): Promise<{ items: Observation[]; total: number }> {
  const allItems: Observation[] = [];
  let page = 1;
  let total = 0;
  const limit = 200;

  while (true) {
    let url = `/api/v2/sites/${siteId}/observations?page=${page}&limit=${limit}`;
    if (filters?.sourceType) url += `&source_type=${encodeURIComponent(filters.sourceType)}`;
    if (filters?.observationType) url += `&observation_type=${encodeURIComponent(filters.observationType)}`;
    if (filters?.assetId) url += `&asset_id=${encodeURIComponent(filters.assetId)}`;
    if (filters?.sessionId) url += `&session_id=${encodeURIComponent(filters.sessionId)}`;
    if (filters?.evidenceId) url += `&evidence_id=${encodeURIComponent(filters.evidenceId)}`;
    if (filters?.severity) url += `&severity=${encodeURIComponent(filters.severity)}`;
    if (filters?.status) url += `&status=${encodeURIComponent(filters.status)}`;

    const result = await apiFetchGet<ObservationListResponse>(url);

    if (page === 1) total = result.total;

    for (const row of result.items) {
      allItems.push(entityRowToObservation(row));
    }

    if (!result.hasMore) break;
    page++;
  }

  return { items: allItems, total };
}

/**
 * Fetch a single observation by ID. Returns null on 404.
 */
export async function fetchSingleObservation(
  siteId: string,
  id: string,
): Promise<Observation | null> {
  try {
    const row = await apiFetchGet<ObservationEntityRow>(
      `/api/v2/sites/${siteId}/observations/${id}`,
    );
    return entityRowToObservation(row);
  } catch {
    return null;
  }
}

// ─── Write API ──────────────────────────────────────────────────────────────────

/**
 * Create an observation. Returns the created observation with version from server.
 */
export async function createObservation(
  siteId: string,
  observation: Partial<Observation> & { sourceType: string; observationType: string },
): Promise<Observation> {
  const body = observationToCreateBody(observation);
  const { data: row, etag } = await apiFetchMutate<ObservationEntityRow>(
    `/api/v2/sites/${siteId}/observations`,
    'POST',
    body,
  );
  const result = entityRowToObservation(row);
  result.version = etag || result.version;
  return result;
}

/**
 * Update an observation. Requires version for optimistic concurrency (If-Match).
 * Throws 409 on version conflict — caller must handle via isConflictError().
 */
export async function updateObservation(
  siteId: string,
  id: string,
  updates: {
    severity?: string;
    status?: string;
    confidence?: number;
    geometry?: GeoJSON.Geometry;
    metrics?: Record<string, unknown>;
    topology?: Record<string, string>;
    properties?: Record<string, unknown>;
  },
  version: number,
): Promise<Observation> {
  const patchBody: Record<string, unknown> = {};
  if (updates.severity !== undefined) patchBody.severity = updates.severity;
  if (updates.status !== undefined) patchBody.status = updates.status;
  if (updates.confidence !== undefined) patchBody.confidence = updates.confidence;
  if (updates.geometry !== undefined) patchBody.geometry_json = JSON.stringify(updates.geometry);
  if (updates.metrics !== undefined) patchBody.metrics_json = JSON.stringify(updates.metrics);
  if (updates.topology !== undefined) patchBody.topology_json = JSON.stringify(updates.topology);
  if (updates.properties !== undefined) patchBody.properties_json = JSON.stringify(updates.properties);

  const { data: row, etag } = await apiFetchMutate<ObservationEntityRow>(
    `/api/v2/sites/${siteId}/observations/${id}`,
    'PATCH',
    patchBody,
    { 'If-Match': String(version) },
  );
  const result = entityRowToObservation(row);
  result.version = etag || result.version;
  return result;
}
